"use client";

import { useEffect, useState } from "react";
import {
  Music,
  Mic2,
  TrendingUp,
  Shield,
  Waves,
  Volume2,
  Activity,
  GitCompareArrows,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// Real analysis data from SoundBench API (2026-05-29)
const songs = [
  {
    name: "Azra",
    title: "Užas je moja furka",
    duration: "3:11",
    recognizability: 79.9,
    stabilization: 66.7,
    refiguration: "stabilization",
    confidence: 69.5,
    pitch: [82.5, 335.4],
    centroid: 2317,
    dynamic: 0.43,
    vibrato: 0.53,
    voiced: 1.0,
    color: "#6366f1",
  },
  {
    name: "David Bowie",
    title: "Starman",
    duration: "4:10",
    recognizability: 70.9,
    stabilization: 62.1,
    refiguration: "stabilization",
    confidence: 63.6,
    pitch: [84.3, 262.9],
    centroid: 3072,
    dynamic: 0.42,
    vibrato: 0.46,
    voiced: 1.0,
    color: "#f97316",
  },
];

const comparison = {
  authenticity: 72.7,
  summary:
    "Azra (ex-YU rock) and Bowie (UK glam) differ in brightness (+755 Hz) and loudness (+60%) but share some timbral qualities.",
};

const atmosphereDimensions = [
  {
    label: "Energy",
    azra: 73,
    bowie: 68,
    note: "Both high-energy rock vocals",
  },
  {
    label: "Brightness",
    azra: 58,
    bowie: 77,
    note: "Bowie is noticeably brighter",
  },
  {
    label: "Tension",
    azra: 67,
    bowie: 62,
    note: "Azra has wider pitch range",
  },
  {
    label: "Warmth",
    azra: 71,
    bowie: 65,
    note: "Azra has richer lower harmonics",
  },
];

const voiceProfileFields = [
  { key: "pitch" as const, label: "Pitch", format: (s: (typeof songs)[0]) => `${s.pitch[0].toFixed(0)}–${s.pitch[1].toFixed(0)} Hz` },
  { key: "centroid" as const, label: "Brightness", format: (s: (typeof songs)[0]) => `${s.centroid.toFixed(0)} Hz` },
  { key: "dynamic" as const, label: "Dynamics", format: (s: (typeof songs)[0]) => s.dynamic.toFixed(2) },
  { key: "vibrato" as const, label: "Vibrato", format: (s: (typeof songs)[0]) => s.vibrato.toFixed(2) },
  { key: "voiced" as const, label: "Voiced", format: (s: (typeof songs)[0]) => `${(s.voiced * 100).toFixed(0)}%` },
];

function ScoreBar({
  label,
  value,
  color,
  animate,
}: {
  label: string;
  value: number;
  color: string;
  animate: boolean;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 6,
          fontSize: "0.8125rem",
        }}
      >
        <span style={{ color: "var(--text-secondary)", fontWeight: 500 }}>{label}</span>
        <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {value.toFixed(1)}
        </span>
      </div>
      <div className="score-bar-track">
        <div
          className="score-bar-fill"
          style={{
            width: animate ? `${value}%` : "0%",
            background: color,
          }}
        />
      </div>
    </div>
  );
}

function ComparisonBar({
  label,
  value,
  color,
  animate,
}: {
  label: string;
  value: number;
  color: string;
  animate: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
      <span
        style={{
          fontSize: "0.75rem",
          color,
          width: 44,
          fontWeight: 500,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <div className="score-bar-track" style={{ flex: 1 }}>
        <div
          className="score-bar-fill"
          style={{
            width: animate ? `${value}%` : "0%",
            background: color,
          }}
        />
      </div>
      <span
        style={{
          fontSize: "0.75rem",
          width: 28,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
          color: "var(--text-secondary)",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SongCard({ song, index }: { song: (typeof songs)[0]; index: number }) {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimate(true), 150 + index * 100);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <div
      className="glass animate-in"
      style={{
        flex: 1,
        minWidth: 300,
        padding: 28,
        borderRadius: "var(--radius-lg)",
        animationDelay: `${index * 0.1}s`,
      }}
    >
      {/* Track header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "var(--radius-sm)",
            background: `${song.color}18`,
            border: `1px solid ${song.color}30`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Music size={20} style={{ color: song.color }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: "1.0625rem",
              letterSpacing: "-0.02em",
            }}
          >
            {song.name}
          </div>
          <div
            style={{
              color: "var(--text-muted)",
              fontSize: "0.8125rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {song.title} · {song.duration}
          </div>
        </div>
      </div>

      {/* Refiguration badge */}
      <div
        className="badge"
        style={{
          background: `${song.color}14`,
          color: song.color,
          border: `1px solid ${song.color}25`,
          marginBottom: 24,
        }}
      >
        <Shield size={12} />
        {song.refiguration} · {song.confidence.toFixed(0)}%
      </div>

      {/* Scores */}
      <div style={{ marginBottom: 8 }}>
        <div
          style={{
            fontSize: "0.6875rem",
            fontWeight: 500,
            color: "var(--text-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            marginBottom: 14,
          }}
        >
          Identity Scores
        </div>
        <ScoreBar
          label="Recognizability"
          value={song.recognizability}
          color={song.color}
          animate={animate}
        />
        <ScoreBar
          label="Stabilization"
          value={song.stabilization}
          color={song.color}
          animate={animate}
        />
      </div>

      {/* Voice profile */}
      <div
        style={{
          marginTop: 20,
          padding: 18,
          borderRadius: "var(--radius-md)",
          background: "rgba(0, 0, 0, 0.25)",
          border: "1px solid var(--border-subtle)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "var(--text-secondary)",
            marginBottom: 14,
            fontSize: "0.75rem",
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          <Mic2 size={13} />
          Voice Profile
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px 20px",
            fontSize: "0.8125rem",
          }}
        >
          {voiceProfileFields.map((field) => (
            <div key={field.key} style={{ display: "contents" }}>
              <span style={{ color: "var(--text-muted)" }}>{field.label}</span>
              <span style={{ fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
                {field.format(song)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AnalyzePage() {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimate(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="page-wrapper">
      <div className="mesh-bg" aria-hidden="true" />

      <Header badge="Demo · Real analysis results" />

      <main className="container" style={{ padding: "48px 24px 64px" }}>
        {/* Page header */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div
            className="animate-in"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 14px",
              marginBottom: 20,
              borderRadius: 100,
              border: "1px solid var(--border-subtle)",
              background: "var(--bg-surface)",
              fontSize: "0.8125rem",
              color: "var(--text-secondary)",
              fontWeight: 500,
            }}
          >
            <Activity size={14} style={{ color: "var(--accent-indigo)" }} />
            Live DSP results
          </div>
          <h1
            className="animate-in animate-in-delay-1"
            style={{
              fontSize: "clamp(1.75rem, 4vw, 2.25rem)",
              fontWeight: 700,
              margin: "0 0 10px",
              letterSpacing: "-0.02em",
            }}
          >
            Song Analysis
          </h1>
          <p
            className="animate-in animate-in-delay-2"
            style={{
              color: "var(--text-secondary)",
              margin: 0,
              fontSize: "0.9375rem",
              maxWidth: 440,
              marginInline: "auto",
            }}
          >
            Real DSP analysis of two tracks — Azra vs David Bowie
          </p>
        </div>

        {/* Song cards */}
        <div
          style={{
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
            marginBottom: 24,
          }}
        >
          {songs.map((song, i) => (
            <SongCard key={song.name} song={song} index={i} />
          ))}
        </div>

        {/* Comparison */}
        <div
          className="glass animate-in animate-in-delay-3"
          style={{
            padding: 32,
            borderRadius: "var(--radius-lg)",
            marginBottom: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 24,
            }}
          >
            <GitCompareArrows size={18} style={{ color: "var(--accent-indigo)" }} />
            <div>
              <h2
                style={{
                  fontSize: "1.0625rem",
                  fontWeight: 600,
                  margin: 0,
                }}
              >
                Comparison
              </h2>
              <p
                style={{
                  color: "var(--text-muted)",
                  fontSize: "0.8125rem",
                  margin: "2px 0 0",
                }}
              >
                Bowie vs Azra (reference)
              </p>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 28,
              flexWrap: "wrap",
              marginBottom: 20,
            }}
          >
            {/* Authenticity ring */}
            <div style={{ position: "relative", width: 100, height: 100, flexShrink: 0 }}>
              <svg width="100" height="100" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="rgba(255,255,255,0.06)"
                  strokeWidth="4"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="url(#authGrad)"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray="283"
                  strokeDashoffset={animate ? 283 - (283 * comparison.authenticity) / 100 : 283}
                  transform="rotate(-90 50 50)"
                  style={{ transition: "stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)" }}
                />
                <defs>
                  <linearGradient id="authGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#6366f1" />
                    <stop offset="100%" stopColor="#34d399" />
                  </linearGradient>
                </defs>
              </svg>
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {comparison.authenticity.toFixed(1)}
                </span>
                <span style={{ fontSize: "0.6875rem", color: "var(--text-muted)" }}>/ 100</span>
              </div>
            </div>

            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <TrendingUp size={16} style={{ color: "var(--success)" }} />
                <span style={{ fontWeight: 600, fontSize: "1rem" }}>Authenticity</span>
              </div>
              <div
                style={{
                  color: "var(--success)",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  marginBottom: 8,
                }}
              >
                Moderate-high similarity
              </div>
              <p
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "0.875rem",
                  margin: 0,
                  maxWidth: 420,
                  lineHeight: 1.6,
                }}
              >
                {comparison.summary}
              </p>
            </div>
          </div>
        </div>

        {/* Atmosphere dimensions */}
        <div
          className="glass animate-in animate-in-delay-4"
          style={{
            padding: 32,
            borderRadius: "var(--radius-lg)",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <Waves size={18} style={{ color: "var(--accent-violet)" }} />
              <h2
                style={{
                  fontSize: "1.0625rem",
                  fontWeight: 600,
                  margin: 0,
                }}
              >
                Atmosphere Dimensions
              </h2>
            </div>
            <p
              style={{
                color: "var(--text-muted)",
                fontSize: "0.8125rem",
                margin: 0,
              }}
            >
              Based on DSP features: energy, brightness, tension, warmth
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 16,
            }}
          >
            {atmosphereDimensions.map((dim) => (
              <div
                key={dim.label}
                style={{
                  padding: 20,
                  borderRadius: "var(--radius-md)",
                  background: "rgba(0, 0, 0, 0.2)",
                  border: "1px solid var(--border-subtle)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontWeight: 600,
                    marginBottom: 16,
                    fontSize: "0.875rem",
                  }}
                >
                  <Volume2 size={14} style={{ color: "var(--text-muted)" }} />
                  {dim.label}
                </div>
                <ComparisonBar
                  label="Azra"
                  value={dim.azra}
                  color="var(--track-a)"
                  animate={animate}
                />
                <ComparisonBar
                  label="Bowie"
                  value={dim.bowie}
                  color="var(--track-b)"
                  animate={animate}
                />
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    marginTop: 10,
                    lineHeight: 1.5,
                  }}
                >
                  {dim.note}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
